import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@fmc-digital/i18n";
import { Button, cn } from "@fmc-digital/ui";
import { getIcon } from "@fmc-digital/assets";
import { ROUTES } from "@/constants/routes.constants";
import { buildImageUrl } from "@/utils/imageUrl";
import {
  trackRegistrationValuePropEvent,
  RegistrationValuePropEvent,
} from "@/utils/analytics.utils";
import {
  registrationValuePropCardNavStackStyles,
  registrationValuePropCarouselRowStyles,
  registrationValuePropCtaRowStyles,
  registrationValuePropLoginLinkRowStyles,
  registrationValuePropPageShellStyles,
  registrationValuePropRootStyles,
} from "./registration-value-proposition.styles";
import { resolveValuePropSlides } from "./registration-value-proposition.utils";
import { ValuePropositionSlideCard } from "../../molecules/ValuePropositionSlideCard";
import {
  VALUE_PROPOSITION_AUTOPLAY_MS,
  getValuePropDotKey,
  getValuePropositionSlideImageRelativePath,
} from "./value-proposition-slides.constants";

/**
 * Registration marketing / value proposition (Figma: Registration - Value Proposition).
 * **Mobile (`< md`)**: no white card — phone + copy + carousel on page background. **`md`+**: inner **518×665** / **566×665** (`lg`); card **582×729** / **630×729** with **32px** padding.
 * Page gutters + vertical padding: `registrationValuePropPageShellStyles`; column width: `registrationValuePropRootStyles`.
 */
export function RegValuePropContainer() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  /** Derive from `t` in render (not in `useState` init) so `useTranslation`/`I18nextProvider` are in sync. */
  const slides = useMemo(
    () => resolveValuePropSlides(i18n, t),
    [t, i18n.language, i18n.resolvedLanguage],
  );
  const [slideIndex, setSlideIndex] = useState(0);
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  const pauseAutoplay = useCallback(() => setAutoplayPaused(true), []);
  const resumeAutoplay = useCallback(() => setAutoplayPaused(false), []);

  const isDev = process.env.NODE_ENV === "development";

  /** If remote (CDN) or merged bundles ship duplicate copy, the UI looks “stuck” while index changes. */
  useEffect(() => {
    if (!isDev) return;
    if (slides.length < 2) return;
    const key = (s: { headline: string; description: string }) => `${s.headline}\0${s.description}`;
    const unique = new Set(slides.map(key));
    if (unique.size < slides.length) {
      // eslint-disable-next-line no-console
      console.warn(
        "[ValueProp] i18n slides contain duplicate headline/description entries; carousel will look static.",
        { count: slides.length, unique: unique.size },
      );
    }
  }, [slides, isDev]);

  useEffect(() => {
    setSlideIndex((i) => {
      if (slides.length === 0) return 0;
      return Math.min(i, slides.length - 1);
    });
  }, [slides.length]);

  const activeSlide =
    slides[Math.min(slideIndex, Math.max(slides.length - 1, 0))] ??
    slides[0] ?? {
      headline: "",
      description: "",
    };

  useEffect(() => {
    if (slides.length <= 1 || autoplayPaused) {
      return;
    }
    // One object per effect run so the interval + updater always share the same flag;
    // set `current` false before `clearInterval` so an already-queued tick cannot schedule
    // `setSlideIndex` after teardown (avoids updates after unmount).
    const autoplayActive = { current: true };
    const id = window.setInterval(() => {
      if (!autoplayActive.current) return;
      const n = slidesRef.current.length;
      if (n <= 1) return;
      if (!autoplayActive.current) return;
      setSlideIndex((i) => {
        if (!autoplayActive.current) return i;
        const nextN = slidesRef.current.length;
        if (nextN <= 1) return i;
        return i >= nextN - 1 ? 0 : i + 1;
      });
    }, VALUE_PROPOSITION_AUTOPLAY_MS);
    return () => {
      autoplayActive.current = false;
      window.clearInterval(id);
    };
  }, [slides.length, autoplayPaused]);

  const slideImageSrc = buildImageUrl(getValuePropositionSlideImageRelativePath(slideIndex));

  const goPrev = useCallback(() => {
    const n = slidesRef.current.length;
    if (n <= 1) return;
    setSlideIndex((i) => (i <= 0 ? n - 1 : i - 1));
  }, []);

  const goNext = useCallback(() => {
    const n = slidesRef.current.length;
    if (n <= 1) return;
    setSlideIndex((i) => (i >= n - 1 ? 0 : i + 1));
  }, []);

  const handleCarouselKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (slidesRef.current.length <= 1) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goPrev();
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goNext();
      }
    },
    [goPrev, goNext],
  );

  const handleRegister = useCallback(() => {
    trackRegistrationValuePropEvent(RegistrationValuePropEvent.REGISTER_CLICK, {
      description: "When 'Register' button is clicked on value propositions.",
    });
    navigate(ROUTES.REGISTER_CREATE_ACCOUNT);
  }, [navigate]);

  const handleDownload = useCallback(() => {
    trackRegistrationValuePropEvent(RegistrationValuePropEvent.DOWNLOAD_APP_CLICK, {
      description: "When 'Download the App' button is clicked on value propositions.",
    });
    navigate(ROUTES.DOWNLOAD_APP);
  }, [navigate]);

  const handleLoginClick = useCallback(() => {
    navigate(ROUTES.LOGIN);
  }, [navigate]);

  return (
    <div
      className={registrationValuePropPageShellStyles()}
      data-testid="registration-value-proposition"
    >
      <div className={registrationValuePropRootStyles()}>
        <div className={registrationValuePropCardNavStackStyles()}>
        <ValuePropositionSlideCard
          slideImageSrc={slideImageSrc}
          slideImageAlt={t("auth.registration.valueProp.phoneMockAlt")}
          slideIndex={slideIndex}
          headline={activeSlide.headline}
          description={activeSlide.description}
          onPhonePointerEnter={pauseAutoplay}
          onPhonePointerLeave={resumeAutoplay}
        />

        <fieldset
          className={cn(
            registrationValuePropCarouselRowStyles(),
            "m-0 min-w-0 border-0 p-0",
          )}
          aria-label={t("auth.registration.valueProp.carouselRegionAria")}
        >
        <button
          type="button"
          onClick={goPrev}
          onKeyDown={handleCarouselKeyDown}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          aria-label={t("auth.registration.valueProp.carouselPreviousAria")}
        >
          <ValuePropCarouselArrow name="arrowLeft" />
        </button>
        <div className="flex items-center gap-1.5" role="tablist">
          {Array.from({ length: slides.length }, (_, i) => {
            const dotKey = getValuePropDotKey(i);
            return (
              <button
                key={dotKey}
                type="button"
                role="tab"
                aria-selected={slideIndex === i}
                className={cn(
                  "h-2 rounded-full border-0 transition-[width] duration-[350ms] ease-out",
                  "outline-none ring-0 ring-offset-0 focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
                  slideIndex === i
                    ? "w-[30px] bg-[rgb(var(--md-sys-color-tertiary-container))]"
                    : "w-2.5 bg-[#e9e9e9] hover:bg-outline",
                )}
                onClick={() => setSlideIndex(i)}
                onKeyDown={handleCarouselKeyDown}
                aria-label={`${i + 1} / ${slides.length}`}
              />
            );
          })}
        </div>
        <button
          type="button"
          onClick={goNext}
          onKeyDown={handleCarouselKeyDown}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          aria-label={t("auth.registration.valueProp.carouselNextAria")}
        >
          <ValuePropCarouselArrow name="arrowRight" />
        </button>
        </fieldset>
        </div>

        <div className={registrationValuePropCtaRowStyles()}>
        <Button
          type="button"
          variant="outlined"
          size="large"
          className="w-full md:w-auto md:min-w-[207px]"
          onClick={handleRegister}
        >
          {t("auth.registration.valueProp.ctaRegister")}
        </Button>
        <Button
          type="button"
          variant="filled"
          size="large"
          className="w-full md:w-auto md:min-w-[207px]"
          onClick={handleDownload}
        >
          {t("auth.registration.valueProp.ctaDownloadApp")}
        </Button>
        </div>

        <div className={registrationValuePropLoginLinkRowStyles()}>
          <button
            type="button"
            onClick={handleLoginClick}
            className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            style={{
              color: "var(--Schemes-On-Background, #191C1E)",
              fontFamily: "Poppins",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: "500",
              lineHeight: "16px",
              letterSpacing: "0.5px",
            }}
          >
            {t("auth.registration.valueProp.loginLinkPrefix")}
            <span
              style={{
                color: "var(--Schemes-Primary, #003049)",
                fontFamily: "var(--Font-Family-body, Poppins)",
                fontSize: "var(--Font-Size-xs, 13px)",
                fontStyle: "normal",
                fontWeight: "500",
                lineHeight: "120%",
                textDecorationLine: "underline",
                textDecorationStyle: "solid",
                textDecorationSkipInk: "none",
                textDecorationThickness: "auto",
                textUnderlineOffset: "auto",
                textUnderlinePosition: "from-font",
                marginLeft: "0.25rem",
              }}
            >
              {t("auth.registration.valueProp.loginLinkAction")}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

/** Carousel arrows using icon registry (matching Figma design) */
function ValuePropCarouselArrow({ name }: { readonly name: "arrowLeft" | "arrowRight" }) {
  const iconSvg = getIcon(name);

  if (!iconSvg) {
    return null;
  }

  return (
    <span
      className={cn(
        "inline-flex h-[12px] w-[12px] shrink-0 items-center justify-center text-inherit",
        "[&>svg]:block [&>svg]:h-[12px] [&>svg]:w-[12px]",
      )}
      aria-hidden
      dangerouslySetInnerHTML={{ __html: iconSvg }}
    />
  );
}
